import { useEffect, useRef, useState } from "react";
import { HeartPulse, Loader2, Video } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * HpRestoreAdButton — inline sidebar rewarded-ad button, shown ONLY when the
 * player's HP has dropped to ≤ 50% of max. Backend also enforces this gate.
 * Watching a short ad restores +30 HP (up to max). Rate-limited by the daily
 * ads cap + minimum cooldown (see server.py _check_ad_rate_limit).
 *
 * Fires: POST /api/ads/reward/hp-restore
 */
export default function HpRestoreAdButton({ run, onRestored }) {
  const [phase, setPhase] = useState("idle"); // idle | playing | granting
  const [countdown, setCountdown] = useState(5);
  const timerRef = useRef(null);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const health   = run?.health ?? 0;
  const maxHealth = run?.max_health ?? 0;
  // Show the offer only when HP is at ≤ 50% (matches backend gate exactly).
  const eligible = run && maxHealth > 0 && health > 0 && health <= Math.round(maxHealth * 0.50 + 0.5);
  if (!eligible) return null;

  const startAd = () => {
    setPhase("playing");
    setCountdown(5);
    timerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(timerRef.current);
          grant();
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  };

  const grant = async () => {
    setPhase("granting");
    try {
      const ad_token = `mock_hp_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      const res = await api.post("/ads/reward/hp-restore", { ad_token, placement: "hp_restore" });
      if (res?.ok) {
        toast.success(`+${res.restored} HP restored (${res.new_health}/${res.max_health})`);
        onRestored?.(res);
        setPhase("idle");
      } else {
        throw new Error("Reward failed");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "HP restore unavailable.";
      toast.error(detail);
      setPhase("idle");
    }
  };

  if (phase === "playing") {
    return (
      <div
        className="panel px-3 py-3 flex items-center gap-2 justify-center w-full"
        data-testid="hp-restore-playing"
        style={{ borderColor: "#e25822" }}
      >
        <Video size={14} className="ember" />
        <span className="text-[10px] tracking-[0.3em] ash-text">DEV AD ·</span>
        <span className="font-heading ember">{countdown}s</span>
      </div>
    );
  }

  if (phase === "granting") {
    return (
      <div className="panel px-3 py-3 flex items-center gap-2 justify-center w-full" data-testid="hp-restore-granting">
        <Loader2 size={12} className="animate-spin" />
        <span className="text-[10px] tracking-[0.3em] ash-text">RESTORING...</span>
      </div>
    );
  }

  return (
    <button
      onClick={startAd}
      data-testid="hp-restore-ad-btn"
      className="panel w-full p-3 flex items-center justify-between hover:border-[#e25822] transition-colors relative overflow-hidden group"
      title="Watch a short ad to restore +30 HP"
      style={{ borderColor: "rgba(226,88,34,0.45)" }}
    >
      {/* Pulsing ember glow to draw the eye when HP is low */}
      <div
        className="absolute inset-0 opacity-40 group-hover:opacity-70 transition-opacity pointer-events-none"
        style={{
          background: "radial-gradient(120px 40px at 20% 50%, rgba(226,88,34,0.35), transparent 70%)",
          animation: "hp-restore-pulse 2.4s ease-in-out infinite",
        }}
      />
      <div className="flex items-center gap-2 relative z-10">
        <HeartPulse size={16} className="ember" />
        <div className="text-left leading-tight">
          <div className="font-heading text-sm text-[#f0e6d2]">Bind the Wound</div>
          <div className="text-[10px] tracking-[0.25em] ash-text">WATCH AD · +30 HP</div>
        </div>
      </div>
      <Video size={12} className="ember opacity-80 relative z-10" />
      <style>{`
        @keyframes hp-restore-pulse {
          0%,100% { opacity: 0.35; }
          50%     { opacity: 0.65; }
        }
      `}</style>
    </button>
  );
}
